import type { ApiError } from "@starsach/shared-types";
import type { Session } from "../auth/auth";
import type { AppConfig } from "../config";
import { viewAsHeaders } from "../viewAs";

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly requestId?: string,
    /** The error schema's `details`, as the API sent it: `ErrorState` shows the shapes it knows (#473). */
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiRequestError";
  }
}

/**
 * The one place a request leaves the app (CLAUDE.md section 11): attaches
 * the ID token, parses the error schema, never uses raw fetch from a
 * component.
 */
export async function fetchJson<T>(config: AppConfig, session: Session, path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${config.apiBaseUrl}${path}`, {
    ...init,
    headers: { ...(init?.headers ?? {}), ...viewAsHeaders(), authorization: `Bearer ${session.idToken}`, accept: "application/json" },
  });
  if (res.ok) return (await res.json()) as T;
  throw await errorOf(res);
}

/** API Gateway's own answers (the JWT authorizer's 401, a throttle) are `{ message }`, not our error schema. */
async function errorOf(res: Response): Promise<ApiRequestError> {
  if (res.status === 401) return new ApiRequestError(401, "SESSION_EXPIRED", SESSION_EXPIRED);
  let body: Partial<ApiError> | undefined;
  try {
    body = (await res.json()) as Partial<ApiError>;
  } catch {
    // no JSON body
  }
  return new ApiRequestError(res.status, body?.error?.code ?? "HTTP_ERROR", body?.error?.message ?? `Request failed (${res.status})`, body?.error?.request_id, body?.error?.details);
}

export const SESSION_EXPIRED = "Your sign-in has expired. Sign in again to carry on.";

/** The same request, for a download the API builds itself (a CSV): the bytes, with the token attached, never a raw fetch from a component. */
export async function fetchBlob(config: AppConfig, session: Session, path: string): Promise<{ blob: Blob; filename: string }> {
  const res = await fetch(`${config.apiBaseUrl}${path}`, { headers: { ...viewAsHeaders(), authorization: `Bearer ${session.idToken}` } });
  if (!res.ok) throw await errorOf(res);
  const filename = /filename="([^"]+)"/.exec(res.headers.get("content-disposition") ?? "")?.[1] ?? "download";
  return { blob: await res.blob(), filename };
}
